import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, UsersIcon, TrendingUpIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
export function Hero() {
  const {
    t
  } = useLanguage();
  return <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-navy" data-id="element-164">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 z-0 opacity-10 bg-grid-pattern animate-[pulse_8s_ease-in-out_infinite]" data-id="element-165" />

      {/* Subtle Gradient Overlay */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-navy/50 via-transparent to-navy" data-id="element-166" />

      {/* Glowing Orbs */}
      <div className="absolute top-1/4 -left-64 w-96 h-96 bg-electric rounded-full mix-blend-screen filter blur-[128px] opacity-20 animate-pulse-slow" data-id="element-167" />
      <div className="absolute bottom-1/4 -right-64 w-96 h-96 bg-electric-light rounded-full mix-blend-screen filter blur-[128px] opacity-20 animate-pulse-slow" style={{
      animationDelay: '2s'
    }} data-id="element-168" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-20" data-id="element-169">
        <div className="max-w-4xl" data-id="element-170">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-171">
            <span className="inline-block py-1 px-3 rounded-full bg-electric/10 border border-electric/20 text-electric-light text-sm font-semibold tracking-wide mb-6" data-id="element-172">
              {t('hero_badge')}
            </span>
          </motion.div>

          <motion.h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-173">
            {t('hero_headline_1')}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-light to-electric" data-id="element-174">
              {t('hero_headline_2')}
            </span>
          </motion.h1>

          <motion.p className="text-xl md:text-2xl text-slate-300 font-light mb-10 max-w-2xl" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-175">
            <span className="font-medium text-white" data-id="element-176">{t('hero_subhead')}</span>
            {t('hero_desc')}
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row gap-4 mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-177">
            <a href="#contact" className="inline-flex justify-center items-center gap-2 bg-electric hover:bg-electric-hover text-white px-8 py-4 rounded-md font-semibold text-lg transition-all shadow-[0_0_20px_rgba(30,111,232,0.3)] hover:shadow-[0_0_30px_rgba(30,111,232,0.5)] hover:-translate-y-1" data-id="element-178">
              {t('hero_cta_primary')}
              <ArrowRightIcon className="w-5 h-5" data-id="element-179" />
            </a>
            <a href="#services" className="inline-flex justify-center items-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/10 px-8 py-4 rounded-md font-semibold text-lg transition-all hover:-translate-y-1 backdrop-blur-sm" data-id="element-180">
              {t('hero_cta_secondary')}
            </a>
          </motion.div>

          {/* Trust Badges */}
          <motion.div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-10 border-t border-white/10" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} data-id="element-181">
            <div className="flex items-center gap-3" data-id="element-182">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-electric/10 flex items-center justify-center" data-id="element-183">
                <UsersIcon className="w-6 h-6 text-electric-light" data-id="element-184" />
              </div>
              <div data-id="element-185">
                <div className="text-2xl font-bold text-white" data-id="element-186">
                  {t('hero_stat_1_val')}
                </div>
                <div className="text-sm text-slate-400 font-medium" data-id="element-187">
                  {t('hero_stat_1_label')}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-188">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-electric/10 flex items-center justify-center" data-id="element-189">
                <ShieldCheckIcon className="w-6 h-6 text-electric-light" data-id="element-190" />
              </div>
              <div data-id="element-191">
                <div className="text-2xl font-bold text-white" data-id="element-192">
                  {t('hero_stat_2_val')}
                </div>
                <div className="text-sm text-slate-400 font-medium" data-id="element-193">
                  {t('hero_stat_2_label')}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-194">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-electric/10 flex items-center justify-center" data-id="element-195">
                <TrendingUpIcon className="w-6 h-6 text-electric-light" data-id="element-196" />
              </div>
              <div data-id="element-197">
                <div className="text-2xl font-bold text-white" data-id="element-198">
                  {t('hero_stat_3_val')}
                </div>
                <div className="text-sm text-slate-400 font-medium" data-id="element-199">
                  {t('hero_stat_3_label')}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Diagonal Divider */}
      <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-[0]" data-id="element-200">
        <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px]" data-id="element-201">
          <path d="M1200 120L0 16.48V0h1200v120z" className="fill-white" data-id="element-202"></path>
        </svg>
      </div>
    </section>;
}